import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components/permission-button';
import type { ReferralMeta, ReferralProductType } from '../services/dashboardV3.types';
import CopyCell from './CopyCell';
import { EmptyContent } from 'src/components/empty-content';
import { EllipsisLabel } from 'src/components/ellipsis-label'

const TABLE_BAR_BG = 'rgba(255, 255, 255, 0.04)';

function formatUSD(value: number) {
  return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

interface ReferralDetailDialogProps {
  open: boolean;
  onClose: () => void;
  meta: ReferralMeta | null;
  loading?: boolean;
  exporting?: boolean;
  productType?: ReferralProductType;
  productAccent?: string;
  onExport?: (filter: 'all' | 'direct' | 'team') => void;
}

export const ReferralDetailDialog = ({
  open,
  onClose,
  meta,
  loading,
  exporting,
  productType = 'perps',
  productAccent = '#FFB74D',
  onExport,
}: ReferralDetailDialogProps) => {
  const t = useTranslate();

  const [filter, setFilter] = useState<'all' | 'direct' | 'team'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const copiedText = t('module.dashboardV3.common.copied', { _: 'Copied' });
  const isAgent = meta?.utype === 'agent';

  const getVolumeAfterBindingLabel = () => {
    if (productType === 'prediction_spot') {
      return t('module.dashboardV3.detail.predictionSpotVolumeAfterBinding', {
        _: 'Prediction Spot Volume After Binding',
      });
    }

    if (productType === 'prediction_leverage') {
      return t('module.dashboardV3.detail.predictionLeverageVolumeAfterBinding', {
        _: 'Prediction Leverage Volume After Binding',
      });
    }

    return t('module.dashboardV3.detail.perpsVolumeAfterBinding', {
      _: 'Perpetuals Volume After Binding',
    });
  };

  useEffect(() => {
    setPage(0);
  }, [meta?.code, productType, filter]);

  const subs = useMemo(() => {
    if (!meta) return [];
    if (!isAgent) return meta.subs.filter((item) => item.level === 1);
    if (filter === 'direct') return meta.subs.filter((item) => item.level === 1);
    if (filter === 'team') return meta.subs.filter((item) => item.level > 1);
    return meta.subs;
  }, [filter, isAgent, meta]);

  const pagedSubs = useMemo(
    () => subs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, subs]
  );


  const summaryCards = meta
    ? [
      {
        label: t('module.dashboardV3.detail.directRefs', { _: 'Direct Customers' }),
        value: String(meta.directRefs),
        accent: '#BB86FC',
      },
      {
        label: t('module.dashboardV3.detail.teamRefs', { _: 'Team Size' }),
        value: String(meta.teamRefs),
        accent: '#AB47BC',
      },
      {
        label: t('module.dashboardV3.detail.downlineVolume', {
          _: 'Subordinate Trading Volume',
        }),
        value: formatUSD(meta.downlineVolume),
        accent: '#03DAC6',
      },
      {
        label: t('module.dashboardV3.detail.deposit', { _: 'Subordinate Deposits' }),
        value: formatUSD(meta.deposit),
        accent: '#03DAC6',
      },
      {
        label: t('module.dashboardV3.detail.fee', { _: 'Subordinate Fees' }),
        value: formatUSD(meta.fee),
        accent: '#64B5F6',
      },
      {
        label: t('module.dashboardV3.detail.rebate', { _: 'Rebate Income' }),
        value: formatUSD(meta.rebate),
        accent: '#81C995',
      },
    ]
    : [];

  const filterOptions = [
    { label: t('module.dashboardV3.range.all', { _: 'All' }), value: 'all' as const },
    { label: t('module.dashboardV3.detail.direct', { _: 'Direct Customer (L1)' }), value: 'direct' as const },
    { label: t('module.dashboardV3.detail.team', { _: 'Team (L2+)' }), value: 'team' as const },
  ];

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { bgcolor: '#2D2D2D', color: '#FFFFFF', borderRadius: 1 } }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 1, fontSize: 20, fontWeight: 600 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6">
              {t('module.dashboardV3.detail.title', { _: 'Sub-level Details' })}
            </Typography>

            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', mt: 1 }}>
              {meta ? (
                <>
                  <CopyCell
                    text={meta.platformAddr}
                    shorten
                    start={6}
                    end={4}
                    minWidth={102}
                    copiedText={copiedText}
                  /> · {meta.method}
                </>
              ) : (
                t('module.dashboardV3.detail.loading', { _: 'Loading...' })
              )}
            </Typography>

            {meta && (
              <Typography sx={{ fontSize: 12, color: 'rgba(129,201,149,0.95)', mt: 1 }}>
                {t('module.dashboardV3.detail.code', { _: 'Invitation Code' })}:
                <Box
                  component="span"
                  sx={{ fontFamily: 'Roboto Mono, monospace', color: '#BB86FC', ml: 0.5 }}
                >
                  {meta.code}
                </Box>
              </Typography>
            )}
          </Box>

          <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.72)' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {loading || !meta ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', py: 4 }}>
            {t('module.dashboardV3.detail.loading', { _: 'Loading...' })}
          </Typography>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {summaryCards.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <Paper
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.04)',
                      p: 2,
                      borderRadius: 1,
                      borderTop: `3px solid ${item.accent}`,
                    }}
                  >
                    <EllipsisLabel
                      label={item.label}
                      sx={{
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.6)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.7,
                        mb: 1,
                      }}
                    />
                    <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#FFFFFF' }}>
                      {item.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={
                  isAgent
                    ? t('module.dashboardV3.detail.agentMode', {
                      _: 'Agency Mode: %{directRefs} direct clients, %{teamRefs}-person team including indirect downlines',
                      directRefs: meta.directRefs,
                      teamRefs: meta.teamRefs,
                    })
                    : t('module.dashboardV3.detail.userMode', {
                      _: 'User: %{directRefs} direct referrals',
                      directRefs: meta.directRefs,
                    })
                }
                color={isAgent ? 'warning' : 'primary'}
                variant="outlined"
                sx={{ borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.84)' }}
              />
            </Box>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.75)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                }}
              >
                {t('module.dashboardV3.detail.subList', { _: 'Subordinate User List' })}
              </Typography>

              {isAgent && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
                  {filterOptions.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      size="medium"
                      clickable
                      onClick={() => {
                        setFilter(option.value);
                        setPage(0);
                      }}
                      variant={filter === option.value ? 'filled' : 'outlined'}
                      sx={{
                        borderRadius: 2,
                        borderColor: filter === option.value ? '#BB86FC !important' : 'rgba(255,255,255,.12)',
                        bgcolor: filter === option.value ? 'rgba(187, 134, 252, .16)' : 'transparent',
                        color: filter === option.value ? '#BB86FC !important' : 'rgba(255,255,255,.60)',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Stack>

            <Paper
              sx={{
                bgcolor: TABLE_BAR_BG,
                backgroundImage: 'none',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <TableContainer
                sx={{
                  maxHeight: 400,
                  bgcolor: '#2D2D2D',
                  '&::-webkit-scrollbar': {
                    width: 8,
                    height: 8,
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#2D2D2D',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255,255,255,0.12)',
                    borderRadius: 1,
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(255,255,255,0.18)',
                  },
                }}
              >
                <Table stickyHeader size="small" >
                  <TableHead
                    sx={{
                      '& .MuiTableCell-root': {
                        height: 56,
                        verticalAlign: 'middle',
                        fontSize: 12,
                        backgroundColor: `${TABLE_BAR_BG} !important`,
                        backgroundImage: 'none !important',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                      },
                      '& .MuiTableCell-stickyHeader': {
                        backgroundImage: 'none !important',
                        backgroundColor: 'rgba(36, 36, 36, 0.96) !important',
                        backdropFilter: 'blur(12px)',
                        color: 'rgba(255,255,255,0.72)',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        fontWeight: 600,
                        fontSize: 12,
                        zIndex: 4,
                        top: 0,
                        overflow: 'hidden',
                      },
                    }}
                  >
                    <TableRow sx={{ height: 56 }}>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                        {t('module.dashboardV3.detail.addr', { _: 'User Address' })}
                      </TableCell>

                      {isAgent && (
                        <TableCell sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, textAlign: 'center' }}>
                          {t('module.dashboardV3.detail.level', { _: 'Level' })}
                        </TableCell>
                      )}

                      <TableCell sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
                        {t('module.dashboardV3.detail.bindTime', { _: 'Binding Time' })}
                      </TableCell>

                      <TableCell sx={{ color: productAccent, fontSize: 12, textAlign: 'right' }}>
                        {getVolumeAfterBindingLabel()}
                      </TableCell>

                      <TableCell sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, textAlign: 'right' }}>
                        {t('module.dashboardV3.detail.preBindVol', { _: 'Historical Volume Before Binding' })}
                      </TableCell>

                      <TableCell sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, textAlign: 'right' }}>
                        {t('module.dashboardV3.detail.subDeposit', { _: 'Cumulative Deposits' })}
                      </TableCell>

                      <TableCell sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, textAlign: 'right' }}>
                        {t('module.dashboardV3.detail.subFee', { _: 'Generated Fees' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pagedSubs && pagedSubs?.length ?
                      pagedSubs.map((sub, index) => (
                        <TableRow
                          key={`${sub.addr}-${index}`}
                          sx={{ height: 52, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                        >
                          <TableCell sx={{ fontSize: 11, color: '#FFFFFF', fontFamily: 'Roboto Mono, monospace' }}>
                            <CopyCell
                              text={sub.addr}
                              shorten
                              start={6}
                              end={4}
                              copiedText={copiedText}
                            />
                          </TableCell>

                          {isAgent && (
                            <TableCell sx={{ fontSize: 11, color: 'rgba(255,255,255,0.78)', textAlign: 'center' }}>
                              L{sub.level}
                            </TableCell>
                          )}

                          <TableCell sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                            {sub.bindTime}
                          </TableCell>

                          <TableCell sx={{ fontSize: 14, color: productAccent, textAlign: 'right' }}>
                            {formatUSD(sub.volume)}
                          </TableCell>

                          <TableCell sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textAlign: 'right' }}>
                            {sub.preBindVol > 0 ? formatUSD(sub.preBindVol) : '—'}
                          </TableCell>

                          <TableCell sx={{ fontSize: 11, color: '#FFFFFF', textAlign: 'right' }}>
                            {formatUSD(sub.deposit)}
                          </TableCell>

                          <TableCell sx={{ fontSize: 11, color: '#FFFFFF', textAlign: 'right' }}>
                            {formatUSD(sub.fee)}
                          </TableCell>
                        </TableRow>
                      )) :
                      (
                        <TableRow>
                          <TableCell
                            colSpan={isAgent ? 7 : 6}
                            sx={{
                              height: 220,
                              borderBottom: 'none',
                              p: 0,
                            }}
                          >
                            <Box
                              sx={{
                                height: 220,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <EmptyContent title={t('common.noData', { _: 'No data' })} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={subs.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('module.dashboardV3.common.rowsPerPage', { _: 'Rows per page' })}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
                sx={{
                  backgroundColor: TABLE_BAR_BG,
                  backgroundImage: 'none',
                  color: 'rgba(255,255,255,0.72)',
                  borderTop: '1px solid rgba(255,255,255,0.08)',

                  '.MuiTablePagination-toolbar': {
                    minHeight: 52,
                    px: 2,
                  },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    color: 'rgba(255,255,255,0.72)',
                  },
                  '.MuiTablePagination-selectIcon': {
                    color: 'rgba(255,255,255,0.72)',
                  },
                  '.MuiInputBase-root': {
                    color: 'rgba(255,255,255,0.9)',
                  },
                  '.MuiIconButton-root': {
                    color: 'rgba(255,255,255,0.72)',
                  },
                  '.MuiIconButton-root.Mui-disabled': {
                    color: 'rgba(255,255,255,0.28)',
                  },
                }}
              />
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.5,
          py: 2,
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
        <Button variant="outlined" color="inherit" onClick={onClose} sx={{ color: '#FFFFFF' }}>
          {t('module.dashboardV3.detail.close', { _: 'Close' })}
        </Button>


        <PermissionButton resource="dashboard-v3" action="export">
          <Button
            variant="contained"
            color="secondary"
            disabled={!meta || !pagedSubs?.length || exporting}
            onClick={() => onExport?.(isAgent ? filter : 'direct')}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 0.5 }} />
            {exporting
              ? t('module.dashboardV3.detail.exporting', { _: 'Exporting...' })
              : t('module.dashboardV3.detail.exportSubs', { _: 'Export Sub-data' })}
          </Button>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}